import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import { useAuthenticatedClient } from '@/api/queries'
import { queryKeys } from '@/api/queries'

export type QuotedVsActual = components['schemas']['QuotedVsActualRead']
export type QuotedVsActualRow = components['schemas']['QuotedVsActualRowRead']
export type QuotedVsActualPO = components['schemas']['QuotedVsActualPORead']
export type LeadTimeUpdate = components['schemas']['LeadTimeUpdate']
export type LeadTimeChange = components['schemas']['LeadTimeChangeRead']
export type Group = components['schemas']['QuotedVsActualRead']['group']
export type Measure = 'lead_time' | 'net' | 'freight'

const ROOT = ['quoted-vs-actual'] as const

const SORT_BY_MEASURE = {
  lead_time: 'lead_gap',
  net: 'net_gap',
  freight: 'freight_gap',
} as const

export function useQuotedVsActual(params: {
  group: Group
  measure: Measure
  months: number
  search?: string
  page?: number
  pageSize?: number
  manufacturerUuid?: string
  taxonomyNodeId?: number
  enabled?: boolean
}) {
  const getClient = useAuthenticatedClient()
  const { enabled = true, ...key } = params
  return useQuery({
    queryKey: [...ROOT, 'rows', key],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/quoted-vs-actual', {
        params: {
          query: {
            group: params.group,
            months: params.months,
            sort: SORT_BY_MEASURE[params.measure],
            search: params.search || undefined,
            page: params.page ?? 1,
            page_size: params.pageSize ?? 50,
            manufacturer_uuid: params.manufacturerUuid,
            taxonomy_node_id: params.taxonomyNodeId,
          },
        },
      })
      if (error) throw error
      return data!
    },
    placeholderData: keepPreviousData,
    enabled,
  })
}

export function useQuotedVsActualPurchaseOrders(params: {
  months: number
  manufacturerUuid?: string
  taxonomyNodeId?: number
  productUuid?: string
  enabled: boolean
}) {
  const getClient = useAuthenticatedClient()
  const { enabled, ...key } = params
  return useQuery({
    queryKey: [...ROOT, 'purchase-orders', key],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/quoted-vs-actual/purchase-orders', {
        params: {
          query: {
            months: params.months,
            manufacturer_uuid: params.manufacturerUuid,
            taxonomy_node_id: params.taxonomyNodeId,
            product_uuid: params.productUuid,
          },
        },
      })
      if (error) throw error
      return data!
    },
    enabled,
  })
}

export function useLeadTimeHistory(params: {
  manufacturerUuid?: string
  taxonomyNodeId?: number
  productUuid?: string
  enabled: boolean
}) {
  const getClient = useAuthenticatedClient()
  const { enabled, ...key } = params
  return useQuery({
    queryKey: [...ROOT, 'lead-time-history', key],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/quoted-vs-actual/lead-time-history', {
        params: {
          query: {
            manufacturer_uuid: params.manufacturerUuid,
            taxonomy_node_id: params.taxonomyNodeId,
            product_uuid: params.productUuid,
          },
        },
      })
      if (error) throw error
      return data!
    },
    enabled,
  })
}

export function useUpdateLeadTime() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: LeadTimeUpdate) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/quoted-vs-actual/lead-time', { body })
      if (error) throw error
      return data!
    },
    onSettled: (_data, _error, body) => {
      void queryClient.invalidateQueries({ queryKey: ROOT })
      void queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(body.manufacturer_uuid) })
    },
  })
}
